import * as Sentry from '@sentry/react';

type ChromeMemoryInfo = {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
};

type PerformanceWithMemory = Performance & {
  memory?: ChromeMemoryInfo;
};

type NavigatorWithResourceHints = Navigator & {
  deviceMemory?: number;
  connection?: {
    effectiveType?: string;
    downlink?: number;
    rtt?: number;
    saveData?: boolean;
  };
};

type DocumentWithDiscardState = Document & {
  wasDiscarded?: boolean;
};

type ResourceSample = {
  timestamp: string;
  route: string;
  sessionId: string;
  usedHeapMb?: number;
  totalHeapMb?: number;
  heapLimitMb?: number;
  heapUsagePercent?: number;
  domNodes: number;
  images: number;
  scripts: number;
  iframes: number;
  hidden: boolean;
  visibilityState: DocumentVisibilityState;
  deviceMemoryGb?: number;
  connectionType?: string;
  downlinkMbps?: number;
  rttMs?: number;
  saveData?: boolean;
};

type ResourceUsageOptions = {
  sampleIntervalMs?: number;
  heartbeatIntervalMs?: number;
  warningCooldownMs?: number;
  highHeapUsagePercent?: number;
  highHeapUsedMb?: number;
  highDomNodeCount?: number;
  highLongTaskMs?: number;
};

type SentryMetricsApi = {
  gauge: (
    key: string,
    value: number,
    options?: {
      unit?: string;
      attributes?: Record<string, string | number | boolean>;
    }
  ) => void;
};

type ResourceCheckpointData = Record<string, string | number | boolean | null | undefined>;

declare global {
  interface Window {
    __eposResourceUsageMonitorInstalled__?: boolean;
    __eposResourceUsageSessionId__?: string;
    __eposResourceUsageLastEventAt__?: Record<string, number>;
    __eposResourceUsageLastSample__?: ResourceSample;
  }
}

const SESSION_STORAGE_KEY = 'epos_resource_usage_session_id';
const HEARTBEAT_STORAGE_KEY = 'epos_resource_usage_heartbeat';

const DEFAULT_OPTIONS: Required<ResourceUsageOptions> = {
  sampleIntervalMs: 30_000,
  heartbeatIntervalMs: 15_000,
  warningCooldownMs: 5 * 60_000,
  highHeapUsagePercent: 80,
  highHeapUsedMb: 1_200,
  highDomNodeCount: 50_000,
  highLongTaskMs: 250,
};

const mb = (bytes: number) => Math.round(bytes / 1024 / 1024);

const getSentryMetrics = (): SentryMetricsApi | undefined => {
  const candidate = Sentry as typeof Sentry & { metrics?: SentryMetricsApi };
  return candidate.metrics;
};

const getMetricAttributes = (
  sample: ResourceSample
): Record<string, string | number | boolean> => ({
  route: sample.route,
  hidden: sample.hidden,
  visibilityState: sample.visibilityState,
  connectionType: sample.connectionType ?? 'unknown',
  saveData: sample.saveData ?? false,
});

const recordResourceMetrics = (sample: ResourceSample) => {
  const metrics = getSentryMetrics();
  if (!metrics) return;

  const attributes = getMetricAttributes(sample);

  try {
    if (typeof sample.usedHeapMb === 'number') {
      metrics.gauge('epos.browser.memory.heap_used_mb', sample.usedHeapMb, {
        unit: 'megabyte',
        attributes,
      });
    }

    if (typeof sample.totalHeapMb === 'number') {
      metrics.gauge('epos.browser.memory.heap_total_mb', sample.totalHeapMb, {
        unit: 'megabyte',
        attributes,
      });
    }

    if (typeof sample.heapLimitMb === 'number') {
      metrics.gauge('epos.browser.memory.heap_limit_mb', sample.heapLimitMb, {
        unit: 'megabyte',
        attributes,
      });
    }

    if (typeof sample.heapUsagePercent === 'number') {
      metrics.gauge('epos.browser.memory.heap_usage_percent', sample.heapUsagePercent, {
        unit: 'percent',
        attributes,
      });
    }

    metrics.gauge('epos.browser.dom.nodes', sample.domNodes, { unit: 'none', attributes });
    metrics.gauge('epos.browser.dom.images', sample.images, { unit: 'none', attributes });
    metrics.gauge('epos.browser.dom.iframes', sample.iframes, { unit: 'none', attributes });
  } catch (error) {
    Sentry.addBreadcrumb({
      category: 'resource.metrics',
      level: 'warning',
      message: 'Unable to record resource metrics',
      data: { error: error instanceof Error ? error.message : String(error) },
    });
  }
};

const createSessionId = () => {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }
  } catch {
    Sentry.addBreadcrumb({
      category: 'resource.monitor',
      level: 'info',
      message: 'crypto.randomUUID unavailable for resource session id',
    });
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const getResourceSessionId = () => {
  if (window.__eposResourceUsageSessionId__) {
    return window.__eposResourceUsageSessionId__;
  }

  try {
    const existing = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) {
      window.__eposResourceUsageSessionId__ = existing;
      return existing;
    }

    const created = createSessionId();
    sessionStorage.setItem(SESSION_STORAGE_KEY, created);
    window.__eposResourceUsageSessionId__ = created;
    return created;
  } catch (error) {
    Sentry.addBreadcrumb({
      category: 'resource.monitor',
      level: 'warning',
      message: 'Unable to persist resource session id',
      data: { error: error instanceof Error ? error.message : String(error) },
    });

    const fallback = createSessionId();
    window.__eposResourceUsageSessionId__ = fallback;
    return fallback;
  }
};

const safeJsonParse = <T>(value: string | null): T | undefined => {
  if (!value) return undefined;

  try {
    return JSON.parse(value) as T;
  } catch (error) {
    Sentry.addBreadcrumb({
      category: 'resource.monitor',
      level: 'warning',
      message: 'Unable to parse resource heartbeat',
      data: { error: error instanceof Error ? error.message : String(error) },
    });
    return undefined;
  }
};

const buildSample = (sessionId: string): ResourceSample => {
  const perf = performance as PerformanceWithMemory;
  const memory = perf.memory;
  const nav = navigator as NavigatorWithResourceHints;
  const connection = nav.connection;

  const base: ResourceSample = {
    timestamp: new Date().toISOString(),
    route: `${window.location.pathname}${window.location.search}`,
    sessionId,
    domNodes: document.getElementsByTagName('*').length,
    images: document.images.length,
    scripts: document.scripts.length,
    iframes: document.getElementsByTagName('iframe').length,
    hidden: document.hidden,
    visibilityState: document.visibilityState,
    deviceMemoryGb: nav.deviceMemory,
    connectionType: connection?.effectiveType,
    downlinkMbps: connection?.downlink,
    rttMs: connection?.rtt,
    saveData: connection?.saveData,
  };

  if (!memory) {
    return base;
  }

  return {
    ...base,
    usedHeapMb: mb(memory.usedJSHeapSize),
    totalHeapMb: mb(memory.totalJSHeapSize),
    heapLimitMb: mb(memory.jsHeapSizeLimit),
    heapUsagePercent: Math.round((memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100),
  };
};

const setResourceContext = (sample: ResourceSample) => {
  window.__eposResourceUsageLastSample__ = sample;
  Sentry.setTag('resource_session_id', sample.sessionId);
  Sentry.setTag('resource_route', sample.route);
  Sentry.setContext('resource_usage', sample);
};

const captureResourceWarning = (reason: string, sample: ResourceSample, cooldownMs: number) => {
  const now = Date.now();
  const key = `${reason}:${sample.route}`;
  const lastEventAt = window.__eposResourceUsageLastEventAt__?.[key] ?? 0;

  if (now - lastEventAt < cooldownMs) {
    return;
  }

  window.__eposResourceUsageLastEventAt__ = {
    ...(window.__eposResourceUsageLastEventAt__ ?? {}),
    [key]: now,
  };

  Sentry.withScope((scope) => {
    scope.setLevel('warning');
    scope.setTag('resource_monitor', 'true');
    scope.setTag('resource_reason', reason);
    scope.setTag('resource_session_id', sample.sessionId);
    scope.setTag('resource_route', sample.route);
    scope.setTag('resource_heap_usage_percent', String(sample.heapUsagePercent ?? 'unknown'));
    scope.setContext('resource_usage', sample);
    Sentry.captureMessage(`High resource usage detected: ${reason}`);
  });
};

const evaluateSample = (sample: ResourceSample, options: Required<ResourceUsageOptions>) => {
  setResourceContext(sample);
  recordResourceMetrics(sample);

  if (
    typeof sample.heapUsagePercent === 'number' &&
    sample.heapUsagePercent >= options.highHeapUsagePercent
  ) {
    captureResourceWarning('high_heap_usage_percent', sample, options.warningCooldownMs);
  }

  if (typeof sample.usedHeapMb === 'number' && sample.usedHeapMb >= options.highHeapUsedMb) {
    captureResourceWarning('high_heap_used_mb', sample, options.warningCooldownMs);
  }

  if (sample.domNodes >= options.highDomNodeCount) {
    captureResourceWarning('high_dom_node_count', sample, options.warningCooldownMs);
  }
};

const writeHeartbeat = (sample: ResourceSample, cleanExit: boolean) => {
  try {
    localStorage.setItem(
      HEARTBEAT_STORAGE_KEY,
      JSON.stringify({
        ...sample,
        cleanExit,
        writtenAt: Date.now(),
      })
    );
  } catch (error) {
    Sentry.addBreadcrumb({
      category: 'resource.monitor',
      level: 'warning',
      message: 'Unable to write resource heartbeat',
      data: { error: error instanceof Error ? error.message : String(error) },
    });
  }
};

const reportPreviousUncleanHeartbeat = () => {
  type PreviousHeartbeat = ResourceSample & {
    cleanExit?: boolean;
    writtenAt?: number;
  };

  const previous = safeJsonParse<PreviousHeartbeat>(localStorage.getItem(HEARTBEAT_STORAGE_KEY));
  if (!previous || previous.cleanExit || !previous.writtenAt) {
    return;
  }

  const ageMs = Date.now() - previous.writtenAt;
  if (ageMs > 10 * 60_000) {
    return;
  }

  Sentry.withScope((scope) => {
    scope.setLevel('warning');
    scope.setTag('resource_monitor', 'true');
    scope.setTag('resource_reason', 'possible_abrupt_browser_termination');
    scope.setTag('resource_session_id', previous.sessionId);
    scope.setTag('resource_route', previous.route);
    scope.setContext('resource_usage_previous_session', previous);
    Sentry.captureMessage('Possible abrupt browser/tab termination');
  });
};

const reportDiscardedPage = (sample: ResourceSample) => {
  const discarded = (document as DocumentWithDiscardState).wasDiscarded;
  if (!discarded) return;

  Sentry.withScope((scope) => {
    scope.setLevel('warning');
    scope.setTag('resource_monitor', 'true');
    scope.setTag('resource_reason', 'document_was_discarded');
    scope.setTag('resource_session_id', sample.sessionId);
    scope.setTag('resource_route', sample.route);
    scope.setContext('resource_usage', sample);
    Sentry.captureMessage('Page was discarded by the browser');
  });
};

const installLongTaskObserver = (
  sessionId: string,
  options: Required<ResourceUsageOptions>
): PerformanceObserver | undefined => {
  if (!('PerformanceObserver' in window)) {
    return undefined;
  }

  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.duration < options.highLongTaskMs) continue;

        const sample = buildSample(sessionId);
        Sentry.addBreadcrumb({
          category: 'resource.long_task',
          level: 'warning',
          message: 'Long task detected',
          data: {
            durationMs: Math.round(entry.duration),
            route: sample.route,
            sessionId: sample.sessionId,
          },
        });

        getSentryMetrics()?.gauge(
          'epos.browser.long_task.duration_ms',
          Math.round(entry.duration),
          {
            unit: 'millisecond',
            attributes: getMetricAttributes(sample),
          }
        );

        captureResourceWarning(
          'long_task',
          {
            ...sample,
            timestamp: new Date().toISOString(),
          },
          options.warningCooldownMs
        );
      }
    });

    observer.observe({ entryTypes: ['longtask'] });
    return observer;
  } catch (error) {
    Sentry.addBreadcrumb({
      category: 'resource.monitor',
      level: 'info',
      message: 'Long task observer unavailable',
      data: { error: error instanceof Error ? error.message : String(error) },
    });
    return undefined;
  }
};

export const captureResourceCheckpoint = (
  name: string,
  data: ResourceCheckpointData = {}
): ResourceSample | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const sample = buildSample(getResourceSessionId());
  setResourceContext(sample);
  recordResourceMetrics(sample);

  Sentry.addBreadcrumb({
    category: 'resource.checkpoint',
    level: 'info',
    message: name,
    data: {
      ...data,
      route: sample.route,
      resourceSessionId: sample.sessionId,
      usedHeapMb: sample.usedHeapMb,
      heapUsagePercent: sample.heapUsagePercent,
      domNodes: sample.domNodes,
    },
  });

  return sample;
};

export const installResourceUsageMonitor = (inputOptions: ResourceUsageOptions = {}) => {
  if (typeof window === 'undefined' || window.__eposResourceUsageMonitorInstalled__) {
    return;
  }

  window.__eposResourceUsageMonitorInstalled__ = true;
  const options = { ...DEFAULT_OPTIONS, ...inputOptions };
  const sessionId = getResourceSessionId();

  Sentry.setTag('resource_session_id', sessionId);
  reportPreviousUncleanHeartbeat();

  const initialSample = buildSample(sessionId);
  setResourceContext(initialSample);
  recordResourceMetrics(initialSample);
  reportDiscardedPage(initialSample);
  writeHeartbeat(initialSample, false);

  const sampleIntervalId = window.setInterval(() => {
    const sample = buildSample(sessionId);
    evaluateSample(sample, options);
  }, options.sampleIntervalMs);

  const heartbeatIntervalId = window.setInterval(() => {
    writeHeartbeat(buildSample(sessionId), false);
  }, options.heartbeatIntervalMs);

  const longTaskObserver = installLongTaskObserver(sessionId, options);

  window.addEventListener('pagehide', () => {
    writeHeartbeat(buildSample(sessionId), true);
    window.clearInterval(sampleIntervalId);
    window.clearInterval(heartbeatIntervalId);
    longTaskObserver?.disconnect();
  });
};
